import useSWR from "swr";
// @ts-expect-error pure js
import useAuthenticatedAxios from "./use-authenticated-axios";
// @ts-expect-error pure js
import useAuth from "./use-auth.js";
import useResources from "./use-resources";
import type { Balance } from "../models/balance";
import type {
  BalanceComptable,
  CourbeTresorerie,
  FacturesHonorairesSummary,
  FichierVirementSummary,
  GrandLivre,
  JournalComptable,
  MandatGestion,
  PeriodesComptables,
  PointeReport,
  SoldeMandant,
  TypeCompteComptable,
} from "../models/comptabilite";

function useProReport<T>(
  path: string | null,
  params?: Record<string, unknown>,
) {
  const axios = useAuthenticatedAxios();
  const { auth } = useAuth();
  const { data, error, isLoading, mutate } = useSWR<T>(
    path ? { url: path, options: { params }, userId: auth.userId } : null,
    ({
      url,
      options,
    }: {
      url: string;
      options: { params?: Record<string, unknown> };
    }) =>
      axios
        .get(url, { params: options.params })
        .then((res: { data: T }) => res.data),
  );
  return { data, error, isLoading, mutate };
}

export function useJournalComptable(year: number, month: number) {
  return useProReport<JournalComptable>("pro/compta/journal", { year, month });
}

export function useBalanceComptable(
  year: number,
  month: number,
  type?: TypeCompteComptable,
) {
  return useProReport<BalanceComptable>("pro/compta/balance", {
    year,
    month,
    ...(type ? { type } : {}),
  });
}

export function usePointe(year: number, month: number) {
  return useProReport<PointeReport>("pro/compta/pointe", { year, month });
}

export function useGrandLivre(year: number, month: number) {
  return useProReport<GrandLivre>("pro/compta/grand-livre", { year, month });
}

export const PRELEVEMENT_HONORAIRES = "prelevement_honoraires";

export function usePrelevementsHonoraires() {
  const {
    resources: prelevements,
    isLoading,
    create,
    del,
    mutate,
  } = useResources<Balance>("compte/balances", {
    params: { type: PRELEVEMENT_HONORAIRES },
  });
  return {
    prelevements,
    isLoading,
    creerPrelevement: create,
    supprimerPrelevement: del,
    mutate,
  };
}

export type ExportComptable =
  | "fec"
  | "balance"
  | "journaux"
  | "pointe"
  | "pointe-tresorerie";

export function useExportsComptables() {
  const axios = useAuthenticatedAxios();

  async function genererExport(
    type: ExportComptable,
    payload: Record<string, unknown>,
  ): Promise<string> {
    const response = await axios.post(`pro/compta/exports/${type}`, payload);
    return response.data as string;
  }

  return { genererExport };
}

export function useCourbeTresorerie(
  year: number,
  month: number,
  enabled: boolean,
) {
  return useProReport<CourbeTresorerie>(
    enabled ? "pro/compta/tresorerie" : null,
    {
      year,
      month,
    },
  );
}

export function usePeriodesComptables() {
  const report = useProReport<PeriodesComptables>("pro/compta/periods");
  const axios = useAuthenticatedAxios();

  async function cloturer(year: number, month: number) {
    await axios.post("pro/compta/periods/close", { year, month });
    await report.mutate();
  }

  async function rouvrir(year: number, month: number) {
    await axios.post("pro/compta/periods/reopen", { year, month });
    await report.mutate();
  }

  return { ...report, cloturer, rouvrir };
}

export function useMandatsGestion() {
  const {
    resources: mandats,
    error,
    isLoading,
    create,
    update,
    mutate,
    axios,
  } = useResources<MandatGestion>("pro/mandats");

  async function genererFacturesHonoraires(
    idsProfileBailleur: number[],
    year: number,
    month: number,
  ): Promise<FacturesHonorairesSummary> {
    const response = await axios.post("pro/compta/factures-honoraires", {
      ids_profile_bailleur: idsProfileBailleur,
      year,
      month,
    });
    return response.data as FacturesHonorairesSummary;
  }

  return {
    mandats,
    error,
    isLoading,
    creerMandat: create,
    cloreMandat: (id: number, dateFin: string) =>
      update(id, { date_fin: dateFin }),
    genererFacturesHonoraires,
    mutate,
  };
}

export function useSoldesMandants() {
  return useProReport<SoldeMandant[]>("pro/mandants/soldes");
}

export function useFichierVirement() {
  const axios = useAuthenticatedAxios();

  async function genererFichierVirement(
    idsProfileBailleur: number[],
    dateExecution: string,
  ): Promise<FichierVirementSummary> {
    const response = await axios.post("pro/compta/fichiers-virement", {
      ids_profile_bailleur: idsProfileBailleur,
      date_execution: dateExecution,
    });
    return response.data as FichierVirementSummary;
  }

  return { genererFichierVirement };
}
